/**
 * Dynamic Plugin Loader
 * Lazy loads video.js plugins to reduce initial bundle size
 */

/**
 * Internal dependencies
 */
import { resolveHotspotStyle } from './hotspotStyle';

/**
 * Cache for loaded plugins to avoid duplicate loads
 */
const loadedPlugins = {
	ads: false,
	ima: false,
	flvjs: false,
	quill: false,
	fontAwesome: false,
};

/**
 * Promise cache to prevent concurrent loading of the same plugins
 */
let adsPluginsPromise = null;
let flvPluginPromise = null;
let quillPromise = null;
let fontAwesomePromise = null;

// Cached reference to FontAwesome's `dom` API so icons can be transformed
// explicitly inside a specific document (e.g. the block-editor canvas iframe),
// where the global `dom.watch()` observer + injected CSS never reach.
let fontAwesomeDom = null;

// Ad blockers commonly block the `videojs-contrib-ads` chunk (its filename
// contains "ads"), which rejects the dynamic import with a ChunkLoadError on
// every player init. Ads are optional and playback is unaffected, so warn once
// instead of logging a hard error per player.
let adsLoadWarned = false;

/**
 * Load video.js ads plugins (videojs-contrib-ads and videojs-ima)
 * Only loads when ads are actually needed
 * Uses promise caching to prevent duplicate concurrent loads
 *
 * @return {Promise<void>} Promise that resolves when plugins are loaded
 */
export async function loadAdsPlugins() {
	if ( loadedPlugins.ads && loadedPlugins.ima ) {
		return; // Already loaded
	}

	// Return existing promise if already loading
	if ( adsPluginsPromise ) {
		return adsPluginsPromise;
	}

	adsPluginsPromise = Promise.all( [
		import( /* webpackChunkName: "videojs-contrib-ads-css" */ 'videojs-contrib-ads/dist/videojs.ads.css' ),
		import( /* webpackChunkName: "videojs-ima-css" */ 'videojs-ima/dist/videojs.ima.css' ),
		import( /* webpackChunkName: "videojs-contrib-ads" */ 'videojs-contrib-ads' ),
		import( /* webpackChunkName: "videojs-ima" */ 'videojs-ima' ),
	] ).then( () => {
		loadedPlugins.ads = true;
		loadedPlugins.ima = true;
		adsPluginsPromise = null; // Clear promise cache after success
	} ).catch( ( error ) => {
		adsPluginsPromise = null; // Clear promise cache on error to allow retry
		if ( ! adsLoadWarned ) {
			adsLoadWarned = true;
			// eslint-disable-next-line no-console
			console.warn( 'GoDAM: video ad plugins could not be loaded (often blocked by an ad blocker). Video playback is unaffected.' );
		}
		throw error;
	} );

	return adsPluginsPromise;
}

/**
 * Load FLV.js plugin for FLV video support
 * Only loads when FLV videos are detected
 * Uses promise caching to prevent duplicate concurrent loads
 *
 * @return {Promise<void>} Promise that resolves when plugin is loaded
 */
export async function loadFlvPlugin() {
	if ( loadedPlugins.flvjs ) {
		return; // Already loaded
	}

	// Return existing promise if already loading
	if ( flvPluginPromise ) {
		return flvPluginPromise;
	}

	flvPluginPromise = import( /* webpackChunkName: "videojs-flvjs-es6" */ 'videojs-flvjs-es6' )
		.then( () => {
			loadedPlugins.flvjs = true;
			flvPluginPromise = null; // Clear promise cache after success
		} )
		.catch( ( error ) => {
			flvPluginPromise = null; // Clear promise cache on error to allow retry
			// eslint-disable-next-line no-console
			console.error( 'Failed to load FLV plugin:', error );
			throw error;
		} );

	return flvPluginPromise;
}

/**
 * Load Quill editor for CTA text layers
 * Only loads when CTA layers with rich text are present
 * Uses promise caching to prevent duplicate concurrent loads
 *
 * @return {Promise<Object>} Promise that resolves with Quill module
 */
export async function loadQuill() {
	if ( loadedPlugins.quill ) {
		return window.Quill; // Return cached instance
	}

	// Return existing promise if already loading
	if ( quillPromise ) {
		return quillPromise;
	}

	quillPromise = ( async () => {
		// Load CSS and JS
		await import( /* webpackChunkName: "quill-css" */ 'quill/dist/quill.snow.css' );
		const Quill = await import( /* webpackChunkName: "quill" */ 'quill' );

		loadedPlugins.quill = true;
		window.Quill = Quill.default || Quill;

		return window.Quill;
	} )()
		.then( ( result ) => {
			quillPromise = null; // Clear promise cache after success
			return result;
		} )
		.catch( ( error ) => {
			quillPromise = null; // Clear promise cache on error to allow retry
			// eslint-disable-next-line no-console
			console.error( 'Failed to load Quill:', error );
			throw error;
		} );

	return quillPromise;
}

/**
 * Check if a video source requires FLV plugin
 *
 * @param {string} src - Video source URL or type
 * @return {boolean} True if FLV plugin is needed
 */
export function requiresFlvPlugin( src ) {
	if ( ! src ) {
		return false;
	}

	const lowerSrc = src.toLowerCase();
	return lowerSrc.includes( '.flv' ) ||
		lowerSrc.includes( 'type=flv' ) ||
		lowerSrc.includes( 'video/flv' ) ||
		lowerSrc.includes( 'video/x-flv' );
}

/**
 * Load FontAwesome icons for hotspot layers
 * Only loads when hotspot layers with icons are present
 * Uses promise caching to prevent duplicate concurrent loads
 *
 * @return {Promise<void>} Promise that resolves when FontAwesome is loaded
 */
export async function loadFontAwesome() {
	if ( loadedPlugins.fontAwesome ) {
		return; // Already loaded
	}

	// Return existing promise if already loading
	if ( fontAwesomePromise ) {
		return fontAwesomePromise;
	}

	fontAwesomePromise = ( async () => {
		// Load FontAwesome core and solid icons
		const { library, dom } = await import( /* webpackChunkName: "fontawesome-core" */ '@fortawesome/fontawesome-svg-core' );
		const { fas } = await import( /* webpackChunkName: "fontawesome-icons" */ '@fortawesome/free-solid-svg-icons' );

		library.add( fas );
		dom.watch();
		fontAwesomeDom = dom;

		loadedPlugins.fontAwesome = true;
	} )()
		.then( () => {
			fontAwesomePromise = null; // Clear promise cache after success
		} )
		.catch( ( error ) => {
			fontAwesomePromise = null; // Clear promise cache on error to allow retry
			// eslint-disable-next-line no-console
			console.error( 'Failed to load FontAwesome:', error );
			throw error;
		} );

	return fontAwesomePromise;
}

/**
 * Transform FontAwesome `<i>` icons into inline `<svg>` inside a given node,
 * and ensure the base SVG CSS exists in that node's document.
 *
 * FontAwesome's SVG core auto-transforms icons via a `dom.watch()` observer and
 * injects its CSS, but both are bound to the document the bundle runs in. In the
 * block-editor canvas the icons live inside an iframe, so the observer never
 * sees them and the sizing CSS (`.svg-inline--fa`) is missing there — the icon
 * renders as an unstyled empty box. Call this after creating the icon markup to
 * convert + style them in the right document. Safe to call on the front end too
 * (idempotent: once an `<i>` becomes `<svg>` there is nothing left to convert).
 *
 * @param {HTMLElement} node The subtree whose FontAwesome icons should render.
 * @return {void}
 */
export function renderFontAwesomeIcons( node ) {
	if ( ! fontAwesomeDom || ! node ) {
		return;
	}
	try {
		const doc = node.ownerDocument;
		if (
			doc &&
			doc.head &&
			! doc.getElementById( 'godam-fa-svg-css' ) &&
			typeof fontAwesomeDom.css === 'function'
		) {
			const style = doc.createElement( 'style' );
			style.id = 'godam-fa-svg-css';
			style.textContent = fontAwesomeDom.css();
			doc.head.appendChild( style );
		}
		fontAwesomeDom.i2svg( { node } );
	} catch ( e ) {}
}

/**
 * Check if any hotspots in layers have icons
 *
 * @param {Array} layers - Array of layer configurations
 * @return {boolean} True if any hotspot has an icon
 */
export function hasHotspotsWithIcons( layers ) {
	if ( ! layers || ! Array.isArray( layers ) ) {
		return false;
	}

	return layers.some( ( layer ) => {
		// Regular hotspot layer. Resolve each point's effective style (shared
		// layer-level model for refactored layers, per-hotspot for legacy ones)
		// so detection matches exactly when the player renders a FontAwesome
		// glyph. Previously this only checked the pre-refactor `hotspot.showIcon`
		// flag, so icon-style hotspots on the new model never triggered the
		// FontAwesome load and rendered blank on the frontend. Mirrors the Woo
		// branch below, which already moved to the layer-level style model.
		if ( layer.type === 'hotspot' ) {
			return layer.hotspots?.some( ( hotspot ) => resolveHotspotStyle( layer, hotspot ).icon );
		}

		// Woo layer: layer-level FA icon (new model) or per-hotspot legacy icon
		if ( layer.type === 'woo' ) {
			if ( layer.styleType === 'icon' && layer.icon ) {
				return true;
			}
			return layer.productHotspots?.some( ( hotspot ) => hotspot.icon );
		}

		return false;
	} );
}

/**
 * Get loaded plugins status
 *
 * @return {Object} Object with plugin load status
 */
export function getLoadedPluginsStatus() {
	return { ...loadedPlugins };
}

